import { useState, useEffect } from 'react';
import {
  View,
  Image,
  TextInput,
  Pressable,
  ScrollView,
  KeyboardAvoidingView,
  StyleSheet,
  TouchableWithoutFeedback,
  Keyboard,
} from 'react-native';
import * as Auth from 'aws-amplify/auth';
import {
  heightPercentageToDP as hp,
  widthPercentageToDP as wp,
} from 'react-native-responsive-screen';
import Toast from 'react-native-root-toast';
import { Feather } from '@expo/vector-icons';

import { isIOS } from '../util/platform';
import AppButton from '../components/AppButton.js';
import alert from '../components/alert.js';
import Util from '../util/util.js';
import CustomText from '../components/CustomText';
import { removeToken } from '../util/storage';
import {
  useFormValidation,
  validateEmail,
  validatePassword,
} from '../util/validation/formValidator';
import { useResponsiveStyles } from '../hooks';
import { generateTemporaryPassword } from '../util/user';
import AppStyles from '../AppStyles';
import { fonts } from '../style/fonts';
import { useTranslation } from '../i18n';

const SignUp = (props) => {
  const { t } = useTranslation('auth');

  const { isWeb, isDesktop } = useResponsiveStyles();

  const [code, setCode] = useState('');
  const [updatingUser, setUpdatingUser] = useState(false);
  const [hidePassword, setHidePassword] = useState(true);

  // Focus states for V3 input styling
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [codeFocused, setCodeFocused] = useState(false);

  // Form validation using centralized hook
  // In production: validate email format, auto-generate password
  // In dev mode: only check email not empty (allow fake emails), password optional
  const {
    values,
    isValid: isFormValid,
    setDisplayErrors,
    setFieldValue,
    getFieldError,
  } = useFormValidation(
    { email: '', password: '' },
    {
      // In dev mode: just check not empty (allow fake emails for testing)
      // In production: full email validation
      email: __DEV__
        ? [(v) => (!v.trim() ? t('auth:signUp.emailRequired') : null)]
        : [validateEmail],
      // In dev mode: validate password if provided (min 8 chars), but allow empty
      // In production: skip password validation entirely (will be auto-generated)
      password: __DEV__
        ? [(v) => (v.length > 0 && v.length < 8 ? t('auth:signUp.passwordTooShort') : null)]
        : [],
    }
  );

  useEffect(() => {
    Util.executeQRSignIn('limited', true);
  }, []);

  const enterEmail = (text) => {
    setFieldValue('email', text.toLowerCase());
  };

  const createUser = async () => {
    try {
      setDisplayErrors(true);
      if (!isFormValid) {
        return;
      }
      setUpdatingUser(true);

      // Auto-generate password if empty (always in production, optional in dev)
      const password = values.password?.trim() || generateTemporaryPassword();

      await postCreateUser(values.email, password);

      await Auth.signOut();
      await removeToken();

      props.navigation.navigate('Login');
    } catch (error) {
      setUpdatingUser(false);
      alert(
        `❌ ${t('common:actions.error')}`,
        code ? t('signUp.verifyEmailAndCode') : t('signUp.verifyEmail'),
      );
    }
    setUpdatingUser(false);
  };

  const postCreateUser = async (new_user, temp_password) => {
    new_user = new_user.toLowerCase();
    let new_email = new_user;

    await Util.post('UserManagement', '/users', {
      body: {
        username: new_user,
        password: temp_password,
        email: new_email,
        organization: 'limited',
        code: code,
        roles: ['Reporter'],
        organization_access: ['limited'],
      },
    }).then(() => {
      Toast.show(`✅ ${t('auth:signUp.created')}`, {
        backgroundColor: AppStyles.colorSet.success,
        position: Toast.positions.BOTTOM,
        duration: 2500,
        textColor: '#fff',
      });
    });
  };

  const icon = hidePassword ? 'eye-off' : 'eye';

  // Responsive widths
  const logoWidth = isDesktop ? 320 : 240;
  const logoHeight = isDesktop ? 89 : 67;
  const cardWidth = isDesktop ? wp(45) : wp(90);
  const linkFontSize = isDesktop ? 18 : 16;

  return (
    <View style={styles.safeAreaContainer}>
      {/* Gold accent bar at top - outside SafeAreaView to reach edge */}
      <View style={styles.accentBar} />
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={isIOS() ? 'padding' : undefined}
      >
        <TouchableWithoutFeedback
          onPress={!isWeb ? Keyboard.dismiss : () => {}}
          accessible={false}
        >
          <ScrollView
            contentContainerStyle={styles.scrollContainer}
            keyboardShouldPersistTaps="handled"
          >

            {/* Logo */}
            <Image
              source={require('../assets/images/logo-black.png')}
              style={[styles.logo, { width: logoWidth, height: logoHeight }]}
            />

            {/* Tagline */}
            <CustomText
              style={styles.tagline}
              font="regular"
              size={16}
              color={AppStyles.colorSet.textSecondary}
            >
              {t('auth:signUp.title')}
            </CustomText>

            {/* Form Card */}
            <View style={[styles.formCard, { width: cardWidth }]}>
              {/* Email input - V3 style */}
              <View
                style={[
                  styles.inputContainer,
                  emailFocused && styles.inputContainerFocused,
                ]}
              >
                <Feather
                  name="mail"
                  size={20}
                  color={emailFocused ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textSecondary}
                  style={styles.inputIcon}
                />
                <TextInput
                  value={values.email}
                  onChangeText={enterEmail}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                  placeholder={t('auth:signUp.emailPlaceholder')}
                  placeholderTextColor={AppStyles.colorSet.textMuted}
                  style={styles.inputText}
                  autoCapitalize="none"
                  autoCorrect={false}
                  keyboardType="email-address"
                  textContentType="emailAddress"
                />
              </View>
              {getFieldError('email') && (
                <CustomText style={styles.errorText} size={14} color={AppStyles.colorSet.error}>
                  {getFieldError('email')}
                </CustomText>
              )}

              {/* Password info - production shows message, dev shows input */}
              {!__DEV__ && (
                <View style={[styles.passwordInfoContainer, { marginTop: hp(2) }]}>
                  <Feather
                    name="send"
                    size={16}
                    color={AppStyles.colorSet.brandPrimary}
                    style={{ marginRight: 8 }}
                  />
                  <CustomText
                    style={{ flex: 1 }}
                    font="regular"
                    size={14}
                    color={AppStyles.colorSet.textSecondary}
                  >
                    {t('auth:signUp.tempPasswordHint')}
                  </CustomText>
                </View>
              )}
              {__DEV__ && (
                <>
                  <View
                    style={[
                      styles.inputContainer,
                      passwordFocused && styles.inputContainerFocused,
                      { marginTop: hp(2) },
                    ]}
                  >
                    <Feather
                      name="lock"
                      size={20}
                      color={passwordFocused ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textSecondary}
                      style={styles.inputIcon}
                    />
                    <TextInput
                      value={values.password}
                      onChangeText={(text) => setFieldValue('password', text)}
                      onFocus={() => setPasswordFocused(true)}
                      onBlur={() => setPasswordFocused(false)}
                      placeholder={t('auth:signUp.passwordPlaceholder')}
                      placeholderTextColor={AppStyles.colorSet.textMuted}
                      style={styles.inputText}
                      autoCapitalize="none"
                      autoCorrect={false}
                      secureTextEntry={hidePassword}
                      textContentType="password"
                    />
                    <Pressable onPress={() => setHidePassword(!hidePassword)} style={styles.eyeButton}>
                      <Feather
                        name={icon}
                        size={20}
                        color={AppStyles.colorSet.textSecondary}
                      />
                    </Pressable>
                  </View>
                  <CustomText
                    style={styles.helperText}
                    font="regular"
                    size={14}
                    color={AppStyles.colorSet.textMuted}
                  >
                    {t('auth:signUp.passwordHint')}
                  </CustomText>
                  {getFieldError('password') && (
                    <CustomText style={styles.errorText} size={14} color={AppStyles.colorSet.error}>
                      {getFieldError('password')}
                    </CustomText>
                  )}
                </>
              )}

              {/* Organization code input - V3 style */}
              <View
                style={[
                  styles.inputContainer,
                  codeFocused && styles.inputContainerFocused,
                  { marginTop: hp(2) },
                ]}
              >
                <Feather
                  name="link"
                  size={20}
                  color={codeFocused ? AppStyles.colorSet.brandPrimary : AppStyles.colorSet.textSecondary}
                  style={styles.inputIcon}
                />
                <TextInput
                  value={code}
                  onChangeText={setCode}
                  onFocus={() => setCodeFocused(true)}
                  onBlur={() => setCodeFocused(false)}
                  placeholder={t('auth:signUp.orgCodePlaceholder')}
                  placeholderTextColor={AppStyles.colorSet.textMuted}
                  style={styles.inputText}
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>
              <CustomText
                style={styles.helperText}
                font="regular"
                size={14}
                color={AppStyles.colorSet.textMuted}
              >
                {t('auth:signUp.optional')}
              </CustomText>

              {/* Create User button - V3 style */}
              <View style={{ marginTop: hp(2) }}>
                <AppButton
                  title={t('auth:signUp.createAccount')}
                  isBusy={updatingUser}
                  isEnabled={!updatingUser && isFormValid}
                  onPress={createUser}
                  fullWidth
                />
              </View>
            </View>

            {/* Back to login link */}
            <View style={styles.linksContainer}>
              <Pressable
                onPress={() => props.navigation.navigate('Login')}
                style={[styles.link, { cursor: 'pointer' }]}
              >
                {({ hovered, focused }) => (
                  <>
                    <CustomText font="regular" size={linkFontSize} color={AppStyles.colorSet.textSecondary}>
                      {t('auth:signUp.alreadyHaveAccount')}
                    </CustomText>
                    <CustomText
                      font="semiBold"
                      size={linkFontSize}
                      color={AppStyles.colorSet.brandPrimary}
                      style={(hovered || focused) && { textDecorationLine: 'underline' }}
                    >
                      {t('auth:signUp.signIn')}
                    </CustomText>
                  </>
                )}
              </Pressable>
            </View>
          </ScrollView>
        </TouchableWithoutFeedback>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  safeAreaContainer: {
    flex: 1,
    backgroundColor: AppStyles.colorSet.whiteColor,
  },
  scrollContainer: {
    alignItems: 'center',
    paddingHorizontal: wp(5),
    paddingTop: hp(2),
    paddingBottom: hp(4),
  },
  accentBar: {
    height: 4,
    backgroundColor: AppStyles.colorSet.brandPrimary,
  },
  logo: {
    resizeMode: 'contain',
    marginTop: hp(4),
  },
  tagline: {
    marginTop: hp(1),
    marginBottom: hp(3),
  },
  formCard: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: AppStyles.borderRadii.lg,
    borderWidth: 1,
    borderColor: AppStyles.colorSet.cardBorder,
    padding: AppStyles.spacing.xxl,
    ...AppStyles.styleSet.shadowSmall,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: AppStyles.borderRadii.md,
    paddingHorizontal: AppStyles.spacing.lg,
    paddingVertical: 14,
  },
  inputContainerFocused: {
    backgroundColor: AppStyles.colorSet.inputBackgroundFocused,
    borderWidth: 2,
    borderColor: AppStyles.colorSet.inputBorderFocused,
    paddingHorizontal: 14, // Slightly less to account for border
    paddingVertical: 12,
  },
  inputIcon: {
    marginRight: 12,
  },
  inputText: {
    flex: 1,
    fontFamily: fonts.POPPINS.regular,
    fontSize: 16,
    color: AppStyles.colorSet.textPrimary,
  },
  eyeButton: {
    padding: 4,
  },
  helperText: {
    marginTop: 6,
    marginLeft: 4,
  },
  passwordInfoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: AppStyles.colorSet.inputBackground,
    borderRadius: AppStyles.borderRadii.md,
    paddingHorizontal: AppStyles.spacing.lg,
    paddingVertical: 14,
  },
  errorText: {
    marginTop: 4,
    marginLeft: 4,
  },
  linksContainer: {
    alignItems: 'center',
    marginTop: hp(3),
    width: '100%',
    paddingHorizontal: AppStyles.spacing.md,
  },
  link: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    flexWrap: 'wrap',
    cursor: 'pointer',
  },
});

export default SignUp;
